<template>
  <div
    class="image-360-viewer"
    @mousedown.prevent="startDrag"
    @mousemove.prevent="onDrag"
    @mouseup.prevent="stopDrag"
    @mouseleave.prevent="stopDrag"
    @touchstart.prevent="startTouch"
    @touchmove.prevent="onTouch"
    @touchend.prevent="stopTouch"
    @wheel.prevent="handleZoom"
    @dragstart.prevent
  >
    <img
      v-if="currentImage"
      :src="currentImage"
      :alt="alt"
      class="image-360-viewer__image"
      :style="{
        transform: `translateX(${offset}px) scale(${zoom})`,
        transformOrigin: 'center center'
      }"
      draggable="false"
    />
    <div v-if="loading" class="image-360-viewer__loading">
      <div class="spinner"></div>
    </div>
    <div v-if="withZoom" class="image-360-viewer__controls">
      <button @click="zoomIn" class="zoom-control">+</button>
      <button @click="zoomOut" class="zoom-control">-</button>
      <button @click="resetZoom" class="zoom-control">Reset</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Image360Viewer',
  props: {
    images: {
      type: [Array, Promise],
      required: true
    },
    alt: {
      type: String,
      default: '360° view'
    },
    sensitivity: {
      type: Number,
      default: 1
    },
    minZoom: {
      type: Number,
      default: 1
    },
    maxZoom: {
      type: Number,
      default: 3
    },
    zoomStep: {
      type: Number,
      default: 0.1
    },
    withZoom: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentIndex: 0,
      isDragging: false,
      startX: 0,
      initialX: 0,
      offset: 0,
      loading: true,
      imageList: [],
      zoom: 1,
      initialPinchDistance: 0
    }
  },
  computed: {
    currentImage() {
      return this.imageList[this.currentIndex]
    }
  },
  methods: {
    startDrag(event) {
      event.preventDefault()
      this.isDragging = true
      this.initialX = event.clientX
      this.startX = event.clientX
    },
    onDrag(event) {
      event.preventDefault()
      if (!this.isDragging) return

      const totalDelta = event.clientX - this.initialX
      const imageWidth = this.$el.offsetWidth

      if (Math.abs(totalDelta) * this.sensitivity >= imageWidth / this.imageList.length) {
        const newIndex = this.currentIndex + (event.movementX > 0 ? 1 : -1)
        this.currentIndex = ((newIndex % this.imageList.length) + this.imageList.length) % this.imageList.length
        this.initialX = event.clientX
      }
    },
    stopDrag(event) {
      event.preventDefault()
      this.isDragging = false
    },
    startTouch(event) {
      event.preventDefault()
      if (event.touches.length === 2 && this.withZoom) {
        // Démarrer le zoom
        this.initialPinchDistance = this.getPinchDistance(event.touches)
      } else {
        // Démarrer le drag
        this.isDragging = true
        this.initialX = event.touches[0].clientX
        this.startX = event.touches[0].clientX
      }
    },
    onTouch(event) {
      event.preventDefault()
      if (event.touches.length === 2 && this.withZoom) {
        // Gérer le zoom
        const currentDistance = this.getPinchDistance(event.touches)
        const delta = currentDistance - this.initialPinchDistance
        this.handleZoomDelta(delta)
        this.initialPinchDistance = currentDistance
      } else if (this.isDragging) {
        const totalDelta = event.touches[0].clientX - this.initialX
        const imageWidth = this.$el.offsetWidth

        if (Math.abs(totalDelta) * this.sensitivity >= imageWidth / this.imageList.length) {
          const newIndex = this.currentIndex + (event.touches[0].clientX > this.startX ? 1 : -1)
          this.currentIndex = ((newIndex % this.imageList.length) + this.imageList.length) % this.imageList.length
          this.initialX = event.touches[0].clientX
        }
      }
    },
    stopTouch(event) {
      event.preventDefault()
      this.isDragging = false
      this.initialPinchDistance = 0
    },
    handleZoom(event) {
      if (!this.withZoom) return
      const delta = event.deltaY > 0 ? -this.zoomStep : this.zoomStep
      this.handleZoomDelta(delta)
    },
    handleZoomDelta(delta) {
      const newZoom = Math.min(Math.max(this.zoom + delta, this.minZoom), this.maxZoom)
      this.zoom = newZoom
    },
    zoomIn() {
      this.handleZoomDelta(this.zoomStep)
    },
    zoomOut() {
      this.handleZoomDelta(-this.zoomStep)
    },
    resetZoom() {
      this.zoom = 1
    },
    getPinchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    },
    async preloadImages() {
      this.loading = true
      try {
        const images = await Promise.resolve(this.images)
        this.imageList = images

        const promises = images.map(src => {
          return new Promise((resolve, reject) => {
            const img = new Image()
            img.onload = resolve
            img.onerror = reject
            img.src = src
          })
        })

        await Promise.all(promises)
        this.loading = false
      } catch (error) {
        console.error('Error preloading images:', error)
        this.loading = false
      }
    }
  },
  async mounted() {
    await this.preloadImages()
  },
  watch: {
    images: {
      handler() {
        this.preloadImages()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.image-360-viewer {
  position: relative;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
}

.image-360-viewer:active {
  cursor: grabbing;
}

.image-360-viewer__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.1s ease-out;
  pointer-events: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
}

.image-360-viewer__loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
}

.image-360-viewer__controls {
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: flex;
  gap: 5px;
  z-index: 10;
}

.zoom-control {
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s;
}

.zoom-control:hover {
  background: rgba(255, 255, 255, 1);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
